import React, { useState, useRef, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useTranslation } from 'react-i18next';
import { apiCall } from '../../api';
import { setI18nLanguage } from '../../i18n/config';
import i18n from '../../i18n/config';

// Roles that hold the backend `verify_records` permission (approve/reject).
const ALLOWED_ROLES = [
  'treasury',
  'senior_accounting_clerk',
  'admin',
];

// Normalize a serialized Expense from GET /api/expenses/ into the shape the
// UI expects: lowercase status, a `requestedBy` display name, and a readable
// category name instead of the raw category id.
const normalizeExpense = (e) => ({
  ...e,
  status: (e.status || '').toLowerCase(),
  requestedBy: e.created_by_name || 'Unknown',
  categoryLabel: e.category_name || e.category,
});

const ApproveExpense = ({ ngo, userRole, onBack, onHome, onEditProfile }) => {
  const { user, logout, getAccessTokenSilently } = useAuth0();
  const { t } = useTranslation('approveExpense');
  const { t: tc } = useTranslation('common');

  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');
  const [busyId, setBusyId] = useState(null);

  const [confirm, setConfirm] = useState(null); // { expense, action }
  const [lastAction, setLastAction] = useState(null); // { expense, action }

  // Filter states
  const [search, setSearch] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [requestedBy, setRequestedBy] = useState('');
  const [minAmount, setMinAmount] = useState('');
  const [maxAmount, setMaxAmount] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filtered, setFiltered] = useState(null);

  const isAuthorized = !userRole || ALLOWED_ROLES.includes(userRole);

  // ---- Profile dropdown state ----
  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef(null);

  // ---- Language menu state ----
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const langMenuRef = useRef(null);

  useEffect(() => {
    if (!profileOpen) return;
    const handleClickOutside = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setProfileOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [profileOpen]);

  // Close the language menu on outside click.
  useEffect(() => {
    if (!langMenuOpen) return;
    const handleClickOutside = (e) => {
      if (langMenuRef.current && !langMenuRef.current.contains(e.target)) {
        setLangMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [langMenuOpen]);

  const handleLogout = () => {
    setProfileOpen(false);
    logout({ logoutParams: { returnTo: window.location.origin } });
  };

  const handleEditProfile = () => {
    setProfileOpen(false);
    if (onEditProfile) onEditProfile();
  };

  useEffect(() => {
    if (!isAuthorized) return;
    if (!ngo?.id) return;

    let cancelled = false;

    const fetchExpenses = async () => {
      setLoading(true);
      setError('');
      try {
        const data = await apiCall(
          `/api/expenses/?org_id=${ngo.id}`,
          {},
          getAccessTokenSilently
        );
        if (!cancelled) {
          setExpenses(Array.isArray(data) ? data.map(normalizeExpense) : []);
        }
      } catch (err) {
        if (!cancelled)      setError(tc('error'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchExpenses();

    return () => {
      cancelled = true;
    };
  }, [ngo, isAuthorized, getAccessTokenSilently,tc]);

  const handleApply = () => {
    let results = expenses;
    if (search) results = results.filter(e =>
      (e.purpose || '').toLowerCase().includes(search.toLowerCase()));
    if (fromDate) results = results.filter(e => e.date >= fromDate);
    if (toDate) results = results.filter(e => e.date <= toDate);
    if (requestedBy) results = results.filter(e =>
      (e.requestedBy || '').toLowerCase().includes(requestedBy.toLowerCase()));
    if (minAmount) results = results.filter(e => e.amount >= Number(minAmount));
    if (maxAmount) results = results.filter(e => e.amount <= Number(maxAmount));
    setFiltered(results);
  };

  const handleReset = () => {
    setSearch(''); setFromDate(''); setToDate('');
    setRequestedBy(''); setMinAmount(''); setMaxAmount('');
    setFiltered(null);
  };

  const handleAction = async (expense, action) => {
    const endpoint = action === 'approved' ? 'approve' : 'reject';
    setBusyId(expense.id);
    setActionError('');
    setConfirm(null);
    try {
      const updated = await apiCall(
        `/api/expenses/${expense.id}/${endpoint}/`,
        { method: 'POST' },
        getAccessTokenSilently
      );
      const normalized = updated && typeof updated === 'object'
        ? normalizeExpense(updated)
        : { ...expense, status: action };
      setExpenses(prev => prev.map(e => (e.id === expense.id ? normalized : e)));
      setLastAction({ expense, action });
    } catch (e) {
      setActionError(e.detail || e.error || tc('error'));
    } finally {
      setBusyId(null);
    }
  };

  const displayExpenses = filtered !== null ? filtered : expenses;
  const pendingExpenses = displayExpenses.filter(e => e.status === 'pending');
  const resolvedExpenses = displayExpenses.filter(e => e.status !== 'pending');

  if (!isAuthorized) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="bg-white rounded-2xl border border-gray-200 p-8 max-w-sm w-full text-center">
          <p className="text-sm font-semibold text-gray-900 mb-2">{t('accessRestricted')}</p>
          <p className="text-xs text-gray-500 mb-6">
            {t('accessDeniedMessage')}
          </p>
          {onBack && (
            <button
              onClick={onBack}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-4 py-2.5 text-sm font-medium transition"
            >
              {t('goBack')}
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">

      {/* Top bar */}
      <div className="bg-white border-b border-gray-200 px-4 py-4 flex items-center gap-3">
        {/* Home icon — 20x20, filled house glyph */}
        <button onClick={onHome} className="text-blue-600 hover:text-blue-700 flex-shrink-0" aria-label="Home" title="Home">
          <svg width="20" height="20" viewBox="0 0 24 24" className="fill-current">
            <path d="M12 3.2 3 10v10h6v-6h6v6h6V10l-9-6.8Z" />
          </svg>
        </button>

        {/* Back arrow — 22x22, outlined */}
        <button onClick={onBack} aria-label="Back" className="text-blue-600 hover:text-blue-700">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
            <path d="M19 12H5M5 12l7-7M5 12l7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>

        {/* Top-bar label */}
        <span className="text-sm font-medium text-gray-900 flex-1">{t('title')}</span>

        <div className="flex-1" />

        {/* ---- LANGUAGE TOGGLE ---- */}
        <div ref={langMenuRef} className="relative">
          <button
            onClick={() => setLangMenuOpen((open) => !open)}
            aria-label="Language menu"
            aria-expanded={langMenuOpen}
            className="text-xs font-medium text-gray-600 border border-gray-200 rounded-lg px-2.5 py-1.5 hover:bg-gray-50 transition"
          >
            {i18n.language === 'ml' ? 'മലയാളം' : 'EN'}
          </button>
          {langMenuOpen && (
            <div className="absolute right-0 top-10 w-32 bg-white rounded-xl shadow-lg border border-gray-100 p-2 z-50">
              <button
                onClick={() => {
                  const lang = 'en';
                  setI18nLanguage(lang);
                  localStorage.setItem('zbookkeeping_language', lang);
                  setLangMenuOpen(false);
                  // Persist to backend (fire-and-forget)
                  getAccessTokenSilently({
                    authorizationParams: { audience: process.env.REACT_APP_AUTH0_AUDIENCE },
                  }).then(token => {
                    fetch(`${window.location.origin}/api/accounts/profile/update/`, {
                      method: 'PATCH',
                      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                      body: JSON.stringify({ preferred_language: lang }),
                    }).catch(() => {});
                  }).catch(() => {});
                }}
                className={`w-full text-left rounded-lg px-3 py-2 text-sm transition ${
                  i18n.language === 'en' ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                English
              </button>
              <button
                onClick={() => {
                  const lang = 'ml';
                  setI18nLanguage(lang);
                  localStorage.setItem('zbookkeeping_language', lang);
                  setLangMenuOpen(false);
                  // Persist to backend (fire-and-forget)
                  getAccessTokenSilently({
                    authorizationParams: { audience: process.env.REACT_APP_AUTH0_AUDIENCE },
                  }).then(token => {
                    fetch(`${window.location.origin}/api/accounts/profile/update/`, {
                      method: 'PATCH',
                      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                      body: JSON.stringify({ preferred_language: lang }),
                    }).catch(() => {});
                  }).catch(() => {});
                }}
                className={`w-full text-left rounded-lg px-3 py-2 text-sm transition ${
                  i18n.language === 'ml' ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                മലയാളം
              </button>
            </div>
          )}
        </div>

        {/* Profile avatar button + dropdown */}
        <div ref={profileRef} className="relative">
          <button
            onClick={() => setProfileOpen((open) => !open)}
            aria-label="Account menu"
            aria-expanded={profileOpen}
            className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-xs font-medium"
          >
            {user?.name?.charAt(0).toUpperCase() || 'U'}
          </button>

          {profileOpen && (
            <div className="absolute right-0 top-11 w-64 bg-white rounded-xl shadow-lg border border-gray-100 p-4 z-50">
              <p className="text-sm text-gray-900 truncate">{user?.email}</p>
              <p className="text-sm text-gray-400 truncate mb-4">{user?.email}</p>

              <button
                onClick={handleEditProfile}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-lg py-2.5 text-sm font-medium transition mb-2"
              >
                {tc('editProfile')}
              </button>
              <button
                onClick={handleLogout}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-lg py-2.5 text-sm font-medium transition"
              >
                {tc('logout')}
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-sm mx-auto px-4 py-8">

        {/* Body heading — §2 */}
        <h1 className="text-center text-base font-medium text-blue-600 mb-6 uppercase tracking-wider">
          {t('title')}
        </h1>

        {/* Load / action error banner */}
        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-lg px-3 py-2.5">
            <p className="text-xs text-red-600">{error}</p>
          </div>
        )}
        {actionError && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-lg px-3 py-2.5">
            <p className="text-xs text-red-600">{actionError}</p>
          </div>
        )}

        {/* Last action status banner */}
        {lastAction && (
          <div className={`mb-4 rounded-lg px-3 py-2.5 border ${
            lastAction.action === 'approved'
              ? 'bg-green-50 border-green-200'
              : 'bg-red-50 border-red-200'
          }`}>
            <p className={`text-xs font-semibold mb-1 ${
              lastAction.action === 'approved' ? 'text-green-700' : 'text-red-600'
            }`}>
              {lastAction.action === 'approved' ? '✓ Approved' : '✗ Rejected'}: {lastAction.expense.purpose}
            </p>
            {lastAction.action === 'approved' && (
              <>
                <p className="text-xs text-green-700">→ Status = Approved ✅</p>
                <p className="text-xs text-green-700">→ Added to Transactions</p>
                <p className="text-xs text-green-700">→ Balance updated</p>
              </>
            )}
            {lastAction.action === 'rejected' && (
              <p className="text-xs text-red-600">→ Status = Rejected</p>
            )}
          </div>
        )}

        {/* Search */}
        <div className="mb-4">
          <p className="text-xs text-gray-500 uppercase tracking-widest mb-1.5">
            {t('searchExpenses')}
          </p>
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder={t('searchPlaceholder')}
            className="w-full bg-gray-100 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400 border-0"
          />
        </div>

        {/* Filters toggle */}
        <div className="bg-white border border-gray-200 rounded-xl px-3.5 py-3 mb-4">
          <div
            className="flex justify-between items-center cursor-pointer"
            onClick={() => setShowFilters(!showFilters)}
          >
            <p className="text-xs text-gray-500 uppercase tracking-widest">
              {t('filters')} {showFilters ? '▲' : '▼'}
            </p>
          </div>

          {showFilters && (
            <div className="mt-3">
              {/* Date */}
              <p className="text-xs text-gray-500 mb-1">{t('dateLabel')}</p>
              <div className="flex gap-2 mb-3">
                <input
                  type="date"
                  value={fromDate}
                  onChange={e => setFromDate(e.target.value)}
                  className="flex-1 border border-gray-200 rounded-lg px-2 py-1.5 text-xs"
                />
                <input
                  type="date"
                  value={toDate}
                  onChange={e => setToDate(e.target.value)}
                  className="flex-1 border border-gray-200 rounded-lg px-2 py-1.5 text-xs"
                />
              </div>

              {/* Requested by */}
              <p className="text-xs text-gray-500 mb-1">{t('requestedBy')}</p>
              <input
                type="text"
                value={requestedBy}
                onChange={e => setRequestedBy(e.target.value)}
                placeholder={t('requestedByName')}
                className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs mb-3"
              />

              {/* Amount range */}
              <p className="text-xs text-gray-500 mb-1">{t('amountRange')}</p>
              <div className="flex gap-2 mb-4">
                <input
                  type="number"
                  value={minAmount}
                  onChange={e => setMinAmount(e.target.value)}
                  placeholder={tc('min')}
                  className="flex-1 border border-gray-200 rounded-lg px-2 py-1.5 text-xs"
                />
                <input
                  type="number"
                  value={maxAmount}
                  onChange={e => setMaxAmount(e.target.value)}
                  placeholder={tc('max')}
                  className="flex-1 border border-gray-200 rounded-lg px-2 py-1.5 text-xs"
                />
              </div>

              {/* Apply / Reset */}
              <div className="flex gap-2">
                <button
                  onClick={handleApply}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white rounded-lg py-2 text-xs font-semibold transition"
                >
                  {tc('apply')}
                </button>
                <button
                  onClick={handleReset}
                  className="flex-1 border border-gray-300 text-gray-600 hover:bg-gray-50 rounded-lg py-2 text-xs font-semibold transition"
                >
                  {tc('reset')}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Pending Expense Requests */}
        <p className="text-xs text-gray-500 uppercase tracking-widest mb-3">
          {t('pendingRequests')}
        </p>

        {loading ? (
          <div className="bg-white border border-gray-200 rounded-xl p-5 text-center mb-4">
            <p className="text-sm text-gray-500">{t('loading')}</p>
          </div>
        ) : pendingExpenses.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-xl p-5 text-center mb-4">
            <p className="text-sm text-gray-500">{t('noPending')}</p>
          </div>
        ) : (
          <div className="flex flex-col gap-3 mb-4">
            {pendingExpenses.map(e => (
              <div key={e.id} className="bg-white border border-gray-200 rounded-xl px-3.5 py-3">
                <div className="flex justify-between mb-1">
                  <p className="text-sm font-semibold text-gray-800">{e.purpose}</p>
                  <p className="text-sm font-bold text-blue-600">₹{e.amount}</p>
                </div>
                <p className="text-xs text-gray-500">{t('requestedByLabel')} {e.requestedBy}</p>
                <p className="text-xs text-gray-500">{t('dateLabelValue')} {e.date}</p>
                <p className="text-xs text-gray-500 mb-2.5">{t('categoryLabelValue')} {e.categoryLabel}</p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setConfirm({ expense: e, action: 'approved' })}
                    disabled={busyId === e.id}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed text-white rounded-lg py-2 text-xs font-semibold transition"
                  >
                    {busyId === e.id ? t('working') : t('approve')}
                  </button>
                  <button
                    onClick={() => setConfirm({ expense: e, action: 'rejected' })}
                    disabled={busyId === e.id}
                    className="flex-1 bg-red-500 hover:bg-red-600 disabled:opacity-60 disabled:cursor-not-allowed text-white rounded-lg py-2 text-xs font-semibold transition"
                  >
                    {t('reject')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Resolved expenses */}
        {!loading && resolvedExpenses.length > 0 && (
          <>
            <p className="text-xs text-gray-500 uppercase tracking-widest mb-3">
              {t('resolved')}
            </p>
            <div className="flex flex-col gap-3">
              {resolvedExpenses.map(e => (
                <div
                  key={e.id}
                  className={`bg-white rounded-xl px-3.5 py-3 opacity-80 border ${
                    e.status === 'approved' ? 'border-green-300' : 'border-red-300'
                  }`}
                >
                  <div className="flex justify-between mb-1">
                    <p className="text-sm font-semibold text-gray-800">{e.purpose}</p>
                    <p className="text-sm font-bold text-blue-600">₹{e.amount}</p>
                  </div>
                  <p className="text-xs text-gray-500">Requested by: {e.requestedBy}</p>
                  <p className="text-xs text-gray-500">Date: {e.date}</p>
                  <p className="text-xs text-gray-500 mb-2">Category: {e.categoryLabel}</p>
                  <p className={`text-center text-xs font-bold uppercase ${
                    e.status === 'approved' ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {e.status === 'approved' ? '✓ Approved' : '✗ Rejected'}
                  </p>
                  {e.status === 'approved' && (
                    <div className="mt-1.5">
                      <p className="text-xs text-green-600">→ Status = Approved ✅</p>
                      <p className="text-xs text-green-600">→ Added to Transactions</p>
                      <p className="text-xs text-green-600">→ Balance updated</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Confirmation Modal */}
      {confirm && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 mx-4 w-full max-w-xs text-center shadow-xl">
            <p className="text-sm font-medium text-gray-900 mb-6">{t('confirmTitle')}</p>
            <div className="flex flex-col gap-3">
              <button
                onClick={() => handleAction(confirm.expense, confirm.action)}
                disabled={busyId === confirm.expense.id}
                className={`w-full text-white rounded-lg py-2.5 text-sm font-medium transition disabled:opacity-60 disabled:cursor-not-allowed ${
                  confirm.action === 'approved'
                    ? 'bg-blue-600 hover:bg-blue-700'
                    : 'bg-red-500 hover:bg-red-600'
                }`}
              >
                {confirm.action === 'approved' ? t('confirmApprove') : t('confirmReject')}
              </button>
              <button
                onClick={() => setConfirm(null)}
                className="w-full border border-gray-300 text-gray-600 hover:bg-gray-50 rounded-lg py-2.5 text-sm font-medium transition"
              >
                {t('cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApproveExpense;